import classNames from "classnames";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Product } from "~/services/product";
import ListLayout, { Layout } from "../../layouts/ListLayout";
import ProductItem from "../Items/ProductItem";
import { ProductItemSize } from "../Items/ProductItem/ProductItem";

import Pagination from "../Pagination";
import ProductItemSkeleton from "../Skeletons/ProductItemSkeleton";
import Text from "../Text";
import { trackingViewListItem } from "~/services/tracking";
import useImpressions from "~/hooks/useImpressions";
import { useLazyQuery } from "@apollo/client";
import {
  QUERY_TRACK_VIEW_COLLECTION,
  QUERY_TRACKING_IMPRESSION,
} from "~/data/product/gql";
import SectionTitle from "../SectionTitle";
import ArrowLeft from "~/assets/svgs/arrow-left.svg";
import { getUrlCustom } from "~/utils/theme";
import { PATH } from "~/constants/enum";
import useClient from "~/hooks/useClient";

export interface ListContainerProps {
  children?: ReactNode;
  extra?: ReactNode;
  pageSize?: number;
  loading?: boolean;
  products?: Product[];
  total?: number;
  currentPage?: number;
  search?: string;
  showListLayout?: boolean;
  listKey?: any;
  identifier?: "collection" | "category" | "search" | "related";
  listProductClass?: string;
  productItemClass?: string;
  defaultLayout?: Layout;
  onProductRemove?: (product: Partial<Product>) => void;
  title?: string;
  onEnter?: () => void;
  onImpression?: (products: Product[]) => void;
}

const ProductItemSizeMap: Record<Layout, ProductItemSize> = {
  list: "small",
  "grid-2": "large",
  "grid-3": "medium",
  "grid-4": "small",
  "grid-5": "xsmall",
  "grid-6": "smaller",
};

const ListContainer: FC<ListContainerProps> = ({
  children,
  extra,
  pageSize = 8,
  loading = true,
  products = [],
  total = 0,
  currentPage = 1,
  search,
  showListLayout = true,
  listKey,
  identifier,
  listProductClass,
  defaultLayout = "grid-4",
  title,
  onProductRemove,
  onEnter,
  onImpression,
}) => {
  const router = useRouter();
  const query = router?.query || {};
  const [layout, setLayout] = useState<Layout>(defaultLayout);
  const [page, setPage] = useState(1);
  const [listSkuError, setListSkuError] = useState(new Set());

  const { initializeObserver, trackedSKUs, visibleSKUs, updateTrackedSKUs } =
    useImpressions({
      selector: ".wrap-product-items",
    });
  const isClient = useClient();

  const [relatedTrackingImpression] = useLazyQuery(QUERY_TRACKING_IMPRESSION);
  const [queryTrackingViewCollection] = useLazyQuery(
    QUERY_TRACK_VIEW_COLLECTION
  );

  useEffect(() => {
    if (currentPage) {
      setPage(currentPage);
    }
  }, [currentPage]);

  // Send tracked SKUs to Google Analytics
  const sendImpressions = () => {
    const skusToTrack = Array.from(visibleSKUs).filter(
      (sku) => !trackedSKUs.has(sku) && !listSkuError.has(sku)
    );

    if (skusToTrack.length > 0) {
      trackingViewListItem({
        listKey: identifier === "search" ? search : listKey,
        products: products.filter((product) =>
          skusToTrack.includes(product.sku)
        ),
      });
      if (
        ["search", "collection", "category", "related"].includes(
          identifier as string
        )
      ) {
        relatedTrackingImpression({
          variables: {
            skus: skusToTrack,
            block_id: identifier === "search" ? undefined : listKey,
            type: identifier,
            search_query: identifier === "search" ? listKey : "",
          },
        });
      }
      onImpression?.(
        products.filter((product) => skusToTrack.includes(product.sku))
      );
      updateTrackedSKUs(skusToTrack as string[]);
    }
  };

  useEffect(() => {
    if (visibleSKUs.size > 0) {
      sendImpressions();
    }
  }, [visibleSKUs]);
  const initTrackingViewCollection = useRef(false);
  useEffect(() => {
    if (!!listKey && !initTrackingViewCollection.current) {
      if (
        ["search", "collection", "category", "related"].includes(
          identifier as string
        )
      ) {
        queryTrackingViewCollection({
          variables: {
            block_id: identifier === "search" ? undefined : listKey,
            type: identifier,
            search_query: identifier === "search" ? listKey : "",
          },
        });
      }

      initTrackingViewCollection.current = true;
    }
  }, [listKey]);

  useEffect(() => {
    if (!products.length || !!loading) return;
    setTimeout(() => {
      initializeObserver();
    }, 1000);
  }, [products, loading]);

  const handleOnPageChange = (newPage: number) => {
    router.push(
      {
        query: {
          ...query,
          currentPage: newPage?.toString(),
        },
      },
      undefined,
      { shallow: true }
    );
    window.scrollTo(0, 0);
  };

  const handleFetchComplete = (data: Product, clientOptions: any) => {
    if (!data) {
      setListSkuError((prev) => {
        prev.add(clientOptions?.variables?.sku);
        return new Set(prev);
      });
    }
  };

  useEffect(() => {
    const ProductsListElement = document.getElementById(listKey);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log("ListContainer -> observer -> entry", entry);
            onEnter?.();
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px 0px 10px 0px",
      }
    );

    ProductsListElement && observer.observe(ProductsListElement);

    return () => {
      ProductsListElement && observer.unobserve(ProductsListElement);
    };
  }, []);

  return (
    <div id={listKey} className="flex-1">
      {
        <div className="flex flex-col gap-4">
          {title && <SectionTitle text={title} />}
          <div className="flex flex-wrap items-center gap-6 mb-2">
            <div className="flex-1">{extra}</div>
            {showListLayout && (
              <ListLayout
                layout={layout}
                onChangeLayout={(l) => {
                  setLayout(l);
                }}
              />
            )}
          </div>
          {!loading && isEmpty(products) && (
            <div className="flex items-center justify-center text-center mt-16">
              <Text type="title-3">
                No product found. Please try another keyword
              </Text>
            </div>
          )}
          <div
            className={classNames(
              "gap-2 lg:gap-2 grid grid-cols-2",
              {
                "md:!flex flex-col": layout === "list",
                "md:grid ": layout !== "list",
                "md:grid-cols-2": layout === "grid-2",
                "md:grid-cols-3": layout === "grid-3",
                "md:grid-cols-4": layout === "grid-4",
                "md:grid-cols-5 sm:grid-cols-3": layout === "grid-5",
                "sm:grid-cols-4 lg:grid-cols-6": layout === "grid-6",
              },
              listProductClass
            )}
          >
            {!products?.length && loading
              ? Array.from({ length: pageSize })
                  .fill({} as Partial<Product>)
                  .map((_, index) => <ProductItemSkeleton key={index} />)
              : null}
            {products?.map((pd: Product, index) => (
              <div
                className={classNames(
                  "wrap-product-items transform scale-90 transition-all ease-in-out duration-75",
                  {
                    "scale-100": trackedSKUs.has(pd.sku),
                    ["hidden"]: listSkuError.has(pd.sku),
                  }
                )}
                data-sku={pd.sku}
              >
                <ProductItem
                  size={ProductItemSizeMap[layout]}
                  data={pd}
                  search={search}
                  params={{
                    b: identifier === "search" ? undefined : listKey,
                    search_query: identifier === "search" ? search : undefined,
                    type: identifier,
                    p: index + 1,
                  }}
                  onRemove={onProductRemove && onProductRemove}
                  actions={
                    !!search
                      ? (data: Partial<Product>) => (
                          <>
                            <a
                              href={getUrlCustom({
                                pathname: PATH.Search,
                                query: {
                                  keyword: data?.name,
                                  ref: pd?.sku,
                                },
                              })}
                              className="text-sm font-semibold flex gap-2 items-center"
                            >
                              More like this{" "}
                              <ArrowLeft
                                className="rotate-180 font-semibold"
                                width={16}
                                height={16}
                              />
                            </a>
                          </>
                        )
                      : undefined
                  }
                  onCompleted={handleFetchComplete}
                />
              </div>
            ))}
          </div>
          {total > 1 && (
            <Pagination
              total={total}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={handleOnPageChange}
            />
          )}
        </div>
      }
    </div>
  );
};

export default ListContainer;
